import React from "react";
import { useLeaveTypes } from "../../../../api/queries/leaveQueries";
import { changeLabel, formatDays, formatLeaveDates } from "./leaveFormat";

/** "Cancel request" / "Edit request": from pendingChange, whatever the status. */
export const LeaveChangeTag = ({ request }) =>
  request?.pendingChange ? (
    <span className="status-pill status-pill--amber">
      {changeLabel(request.pendingChange)}
    </span>
  ) : null;

/** Name of the requested leave type (pendingValues only has the id). */
const useRequestedTypeName = (request) => {
  const { data: types = [] } = useLeaveTypes();
  const id = request?.pendingValues?.leaveTypeId;
  if (!id || id === request.leaveTypeId) return request?.leaveType?.name;
  return types.find((t) => t.id === id)?.name || "Another leave type";
};

/** One line for a table row: what is asked. */
export const LeaveChangeSummary = ({ request }) => {
  if (request?.pendingChange === "CANCEL")
    return request.changeReason ? (
      <div className="fs-070 text-slate-600 mt-1">
        Reason: {request.changeReason}
      </div>
    ) : null;
  const v = request?.pendingValues;
  if (request?.pendingChange !== "EDIT" || !v) return null;
  return (
    <div className="fs-070 text-slate-600 mt-1">
      New: {formatLeaveDates(v.startDate, v.endDate)} ·{" "}
      {formatDays(v.totalDays)}
    </div>
  );
};

/**
 * What the employee asks to change. Edit: current vs requested details.
 * Cancel: the reason, and the days that return.
 */
const LeaveChangeDetails = ({ request }) => {
  const requestedType = useRequestedTypeName(request);
  if (request?.pendingChange === "CANCEL") {
    return (
      <div className="fs-085">
        <p className="mb-2">
          The employee asks to cancel this{" "}
          {request.status === "approved" ? "approved leave" : "leave request"}.
          Approving returns {formatDays(request.totalDays)} to their balance.{" "}
          {request.status === "approved"
            ? "Rejecting keeps the leave as approved."
            : "Rejecting keeps the request, which still needs its normal approval."}
        </p>
        <div className="approval-meta-label">Reason</div>
        <div className="approval-note">{request.changeReason || "—"}</div>
      </div>
    );
  }
  const v = request?.pendingValues;
  if (request?.pendingChange !== "EDIT" || !v) return null;
  const rows = [
    ["Leave type", request.leaveType?.name || "—", requestedType || "—"],
    [
      "Dates",
      formatLeaveDates(request.startDate, request.endDate),
      formatLeaveDates(v.startDate, v.endDate),
    ],
    ["Days", formatDays(request.totalDays), formatDays(v.totalDays)],
    ["Reason", request.reason || "—", v.reason || "—"],
  ];
  return (
    <div className="fs-085">
      <p className="mb-2">
        Approving applies the requested details. Rejecting keeps the leave as
        approved.
      </p>
      <div className="table-responsive">
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th scope="col" aria-label="Detail"></th>
              <th scope="col">Current</th>
              <th scope="col">Requested</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, current, requested]) => (
              <tr key={label}>
                <th scope="row" className="fw-semibold text-nowrap">
                  {label}
                </th>
                <td>{current}</td>
                <td className={current !== requested ? "fw-semibold" : ""}>
                  {requested}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveChangeDetails;
